import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, LogOut, User as UserIcon, Shield, EyeOff, Activity, Heart, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStealthToggle } from "../hooks/useStealthToggle";
import { useReaderAppMode } from "../lib/readerAppMode";

const Header = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Pull the toggle directly into the header — the pill used to deep-
  // link to /settings; it's now a one-click escape hatch usable by
  // anyone (guest or logged-in) the moment Stealth is on.
  const { stealth, counts, toggle: toggleStealth } = useStealthToggle();
  const readerApp = useReaderAppMode();
  const onApp = pathname !== "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate("/");
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-[#EADDD7]/60"
      data-testid="app-header"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 gap-2">
        <Link
          to={user ? "/home" : "/"}
          className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0"
          data-testid="logo-home-link"
        >
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shadow-[0_4px_12px_rgba(155,106,108,0.28)] shrink-0">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <span className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2C2123] leading-none">
            TruBeauté
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-4 shrink-0">
          {/* "Home" link redundant with logo — hidden on mobile to save space. */}
          {onApp && (
            <Link
              to={user ? "/home" : "/"}
              className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2"
              data-testid="nav-home"
            >
              {t("nav.home")}
            </Link>
          )}
          {user && (
            <Link
              to="/my-routines"
              className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2"
              data-testid="nav-my-routines"
            >
              {t("nav.myRoutines")}
            </Link>
          )}
          {user && (
            <Link
              to="/arsenal"
              className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2"
              data-testid="nav-arsenal"
            >
              {t("nav.arsenal")}
            </Link>
          )}
          {user && (
            <Link
              to="/techniques"
              className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2"
              data-testid="nav-techniques"
            >
              Techniques
            </Link>
          )}
          {/* Pricing link — hidden in iOS Reader-app mode per Apple
              Guideline 3.1.3. Hidden on mobile to keep the brand,
              Resources, and sign-in controls from overflowing; signed-in
              members still have Pricing in the user menu. */}
          {!readerApp && (
            <Link
              to="/pricing"
              className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-2 sm:px-3 py-2"
              data-testid="nav-pricing"
            >
              {t("nav.pricing")}
            </Link>
          )}
          {/* Affirming Resources — a first-class mission link.
              Visible to guests on both desktop AND mobile because it's
              a core public surface. For logged-in users it's hidden on
              mobile to keep the header tight; the user menu below
              carries it as the "hamburger" entry. */}
          <Link
            to="/resources"
            className={`text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-2 sm:px-3 py-2 ${user ? "hidden sm:inline-block" : ""}`}
            data-testid="nav-resources"
          >
            <span className="sm:hidden">{t("nav.resourcesShort")}</span>
            <span className="hidden sm:inline">{t("nav.resources")}</span>
          </Link>
          {!user && <Link to="/lessons" className="hidden sm:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2">Lessons</Link>}
          {!user && <Link to="/enterprise" className="hidden lg:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2">{t("nav.enterprise", { defaultValue: "Enterprise" })}</Link>}
          {!user && <Link to="/partners" className="hidden xl:inline-block text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-3 py-2">Partners</Link>}
          {stealth && (
            <button
              type="button"
              onClick={() => toggleStealth(false)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#FAF6F2] bg-[#2C2123] rounded-full px-3 py-1.5 hover:bg-[#3a2b2e] transition-colors"
              data-testid="header-stealth-pill"
              title={
                counts.routines > 0
                  ? t("stealth.pillTitleOnWithCount", {
                      routines: counts.routines,
                    })
                  : t("stealth.pillTitleOn")
              }
              aria-label={t("stealth.pillTitleOn")}
            >
              <EyeOff className="h-3 w-3" />
              <span>{t("stealth.pillLabel")}</span>
              <X className="h-3 w-3 opacity-70" />
            </button>
          )}
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 bg-white border border-[#EADDD7] hover:border-[#D5A8A4] transition-all"
                data-testid="header-user-menu-btn"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-7 w-7 rounded-full object-cover"
                  />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-[#F2E8E9] text-[#9B6A6C] text-xs font-medium flex items-center justify-center">
                    {initials}
                  </span>
                )}
                <span className="text-sm text-[#2C2123] hidden sm:inline">
                  {user.name?.split(" ")[0] || "You"}
                </span>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white border border-[#EADDD7] rounded-2xl soft-shadow-lg overflow-hidden"
                  data-testid="header-user-menu"
                >
                  <div className="p-4 border-b border-[#F5EFEB]">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="h-10 w-10 rounded-full bg-[#F2E8E9] text-[#9B6A6C] font-medium flex items-center justify-center">
                          {initials}
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#2C2123] truncate">
                          {user.name || "You"}
                        </div>
                        <div className="text-xs text-[#8A7D82] truncate">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/my-routines"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-my-routines"
                  >
                    <UserIcon className="h-4 w-4" />
                    {t("myAccount.myGlowJournal")}
                  </Link>
                  <Link
                    to="/arsenal"
                    onClick={() => setMenuOpen(false)}
                    className="flex sm:hidden items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-arsenal"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("nav.arsenal")}
                  </Link>
                  <Link
                    to="/techniques"
                    onClick={() => setMenuOpen(false)}
                    className="flex sm:hidden items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-techniques"
                  >
                    <Activity className="h-4 w-4" />
                    Techniques
                  </Link>
                  {!readerApp && (
                    <Link
                      to="/pricing"
                      onClick={() => setMenuOpen(false)}
                      className="flex sm:hidden items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                      data-testid="header-menu-pricing"
                    >
                      <Activity className="h-4 w-4" />
                      {t("nav.pricing")}
                    </Link>
                  )}
                  <Link
                    to="/resources"
                    onClick={() => setMenuOpen(false)}
                    className="flex sm:hidden items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-resources"
                  >
                    <Heart className="h-4 w-4" />
                    {t("nav.resources")}
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-settings"
                  >
                    <Shield className="h-4 w-4" />
                    {t("myAccount.privacyAndData")}
                  </Link>
                  <Link
                    to="/my-trouve-chats"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-trouve-chats"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("trouveChat.buttonLabel")}
                  </Link>
                  {user.is_ambassador && (
                    <Link
                      to="/ambassador"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-[#5D4050] hover:bg-[#F2E8E9] transition-colors border-t border-[#F5EFEB]"
                      data-testid="header-menu-ambassador"
                    >
                      <Heart className="h-4 w-4" fill="currentColor" />
                      {t("ambassador.navLabel")}
                    </Link>
                  )}
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-[#5D4050] hover:bg-[#F2E8E9] transition-colors border-t border-[#F5EFEB]"
                      data-testid="header-menu-admin"
                    >
                      <Activity className="h-4 w-4" />
                      {t("myAccount.adminDashboard")}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#5C4B51] hover:bg-[#F5EFEB] transition-colors"
                    data-testid="header-menu-logout"
                  >
                    <LogOut className="h-4 w-4" />
                    {t("nav.signOut")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="text-sm text-white bg-[#9B6A6C] hover:bg-[#805557] rounded-full px-3 sm:px-4 py-2 transition-all shadow-[0_4px_14px_rgba(155,106,108,0.3)] hover:-translate-y-0.5 whitespace-nowrap"
              data-testid="nav-login"
            >
              {t("nav.signIn")}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
